import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  useGetFulfillmentGroupDetailsQuery,
  useUpdateProductConditionsMutation 
} from '../../../redux/apis/endpoints/fulfillmentGroupApi';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import Button from '../../../components/ui/Button';
import FormInput from '../../../components/ui/FormInput';
import PriceInputUI from '../../../components/input/PriceInputUI';
import RentDurationInputUI from '../../../components/input/RentDurationInputUI';
import { ArrowLeftIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { CONDITION_OPTIONS } from '../../../constants/conditions';

const ProductConditionUpdatePage = () => {
  const { fulfillmentGroupId } = useParams();
  const history = useHistory();
  const user = useSelector(state => state.auth.user);
  
  // Fetch fulfillment group details
  const { 
    data: fulfillmentGroup, 
    isLoading: isLoadingFG, 
    error: fgError 
  } = useGetFulfillmentGroupDetailsQuery(fulfillmentGroupId);
  
  // Mutation for updating product conditions
  const [updateProductConditions, { isLoading: isUpdating }] = useUpdateProductConditionsMutation();
  
  // State for form data
  const [productUpdates, setProductUpdates] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize form data when fulfillment group loads
  useEffect(() => {
    if (fulfillmentGroup?.orderItems) {
      const initialUpdates = {};
      fulfillmentGroup.orderItems.forEach(item => {
        if (item.status === 'return_delivered') {
          const product = item.product;
          initialUpdates[item.id] = {
            condition: product?.condition || 'good',
            newPrice: product?.price || 0,
            // Initialize rental pricing if product is for rent
            rentPriceAndTime: product?.forRent && product?.rentPriceAndTime 
              ? [...product.rentPriceAndTime] 
              : [],
            notes: product?.notes || '',
            readyForRelisting: true
          };
        }
      });
      setProductUpdates(initialUpdates);
    }
  }, [fulfillmentGroup]);
  
  // Handle form field changes
  const handleFieldChange = (orderItemId, field, value) => {
    setProductUpdates(prev => ({
      ...prev,
      [orderItemId]: {
        ...prev[orderItemId],
        [field]: value
      }
    }));
  };

  // Handle rental price changes
  const handleRentPriceChange = (orderItemId, index, field, value) => {
    setProductUpdates(prev => {
      const currentUpdates = prev[orderItemId] || {};
      const currentRentPrices = [...(currentUpdates.rentPriceAndTime || [])];
      
      // Handle numeric inputs consistently
      const processedValue = (field === 'price' || field === 'number') 
        ? (value === '' ? '' : Number(value))
        : value;

      currentRentPrices[index] = {
        ...currentRentPrices[index],
        [field]: processedValue
      };

      return {
        ...prev,
        [orderItemId]: {
          ...currentUpdates,
          rentPriceAndTime: currentRentPrices
        }
      };
    });
  };

  // Add new rental price option
  const addRentPrice = (orderItemId, currency) => {
    setProductUpdates(prev => {
      const currentUpdates = prev[orderItemId] || {};
      const currentRentPrices = [...(currentUpdates.rentPriceAndTime || [])];
      
      return {
        ...prev,
        [orderItemId]: {
          ...currentUpdates,
          rentPriceAndTime: [
            ...currentRentPrices,
            {
              number: '',
              option: 'day',
              price: '',
              currency: currency || 'USD'
            }
          ]
        }
      };
    });
  };

  // Remove rental price option
  const removeRentPrice = (orderItemId, index) => {
    setProductUpdates(prev => {
      const currentUpdates = prev[orderItemId] || {};
      const currentRentPrices = [...(currentUpdates.rentPriceAndTime || [])];
      currentRentPrices.splice(index, 1);
      
      return {
        ...prev,
        [orderItemId]: {
          ...currentUpdates,
          rentPriceAndTime: currentRentPrices
        }
      };
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const updates = Object.entries(productUpdates).map(([orderItemId, data]) => {
        const orderItem = fulfillmentGroup.orderItems.find(item => item.id === parseInt(orderItemId));
        const product = orderItem?.product;
        
        const update = {
          orderItemId: parseInt(orderItemId),
          condition: data.condition,
          notes: data.notes,
          readyForRelisting: data.readyForRelisting
        };

        // Add price for sale products
        if (product?.forSell && data.newPrice !== undefined && data.newPrice !== null) {
          update.newPrice = parseFloat(data.newPrice) || 0;
        }

        // Add rental pricing for rent products
        if (product?.forRent && data.rentPriceAndTime && data.rentPriceAndTime.length > 0) {
          // Validate and clean rental pricing data
          const validRentPrices = data.rentPriceAndTime.filter(rp => 
            rp.price && rp.number && rp.option
          ).map(rp => ({
            price: parseFloat(rp.price) || 0,
            number: parseInt(rp.number) || 1,
            option: rp.option,
            currency: rp.currency || product.currency || 'USD'
          }));
          
          if (validRentPrices.length > 0) {
            update.rentPriceAndTime = validRentPrices;
          }
        }

        return update;
      });
      
      await updateProductConditions({
        fulfillmentGroupId: parseInt(fulfillmentGroupId),
        productUpdates: updates
      }).unwrap();
      
      toast.success('Product conditions updated successfully!');
      history.push('/dashboard/transactions');
    } catch (error) {
      console.error('Failed to update product conditions:', error);
      toast.error(error.data?.message || 'Failed to update product conditions');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle back navigation
  const handleBack = () => {
    history.goBack();
  };
  
  if (isLoadingFG) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  if (fgError || !fulfillmentGroup) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">
            {fgError?.data?.message || 'Failed to load fulfillment group details'}
          </p>
          <Button onClick={handleBack} variant="secondary">
            Go Back
          </Button>
        </div>
      </div>
    );
  }
  
  // Filter returned items
  const returnedItems = fulfillmentGroup.orderItems?.filter(
    item => item.status === 'return_delivered'
  ) || [];
  
  if (returnedItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Returned Items</h2>
          <p className="text-gray-600 mb-4">
            There are no returned items in this fulfillment group to assess.
          </p>
          <Button onClick={handleBack} variant="secondary">
            Go Back
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Dashboard
          </button>
          
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Update Product Conditions
            </h1>
            <p className="text-gray-600">
              Assess the condition of returned items and update their pricing if needed.
            </p>
            <div className="mt-4 flex items-center text-sm text-gray-500">
              <span className="font-medium">Fulfillment Group:</span>
              <span className="ml-2">#{fulfillmentGroup.id}</span>
            </div>
          </div>
        </div>
        
        {/* Product Condition Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {returnedItems.map((orderItem) => {
            const product = orderItem.product;
            const updates = productUpdates[orderItem.id] || {};
            
            return (
              <div key={orderItem.id} className="bg-white rounded-lg shadow-sm border p-6">
                {/* Product Header */}
                <div className="flex items-start space-x-4 mb-6">
                  {product.photos?.[0] && (
                    <img
                      src={product.photos[0].url}
                      alt={product.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {product.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Size: {product.size} • Original Price: {product.price} {product.currency || 'USD'}
                    </p>
                    <p className="text-sm text-gray-500">
                      Original Condition: {product.condition}
                    </p>
                  </div>
                </div>
                
                {/* Condition Assessment Form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Current Condition */}
                  <div>
                    <FormInput
                      as="select"
                      label="Current Condition"
                      name={`condition-${orderItem.id}`}
                      value={updates.condition || 'good'}
                      onChange={(e) => handleFieldChange(orderItem.id, 'condition', e.target.value)}
                      options={CONDITION_OPTIONS.map(cond => ({ 
                        key: cond.value, 
                        value: cond.value, 
                        text: cond.label 
                      }))}
                      required
                    />
                  </div>

                  {/* Pricing Section - Different for Sale vs Rent */}
                  {product.forSell && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Sale Price
                      </label>
                      <PriceInputUI
                        value={updates.newPrice !== undefined ? updates.newPrice : product.price}
                        onChange={(e) => handleFieldChange(orderItem.id, 'newPrice', e.target.value)}
                        currency={product.currency || 'USD'}
                        disableCurrencyChange={true}
                        required
                      />
                    </div>
                  )}

                  {product.forRent && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Rental Pricing
                      </label>
                      
                      {/* Rental Periods */}
                      <div className="space-y-4">
                        {(updates.rentPriceAndTime || []).map((rentPrice, index) => (
                          <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-sm font-medium text-gray-700">
                                Rental Option {index + 1}
                              </h5>
                              {updates.rentPriceAndTime.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeRentPrice(orderItem.id, index)}
                                  className="text-red-500 hover:text-red-700 text-sm"
                                >
                                  Remove
                                </button>
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <PriceInputUI
                                  name={`rentPrice-${orderItem.id}-${index}`}
                                  value={rentPrice.price || ''}
                                  onChange={(e) => handleRentPriceChange(orderItem.id, index, 'price', e.target.value)}
                                  currency={product.currency || 'USD'}
                                  disableCurrencyChange={true}
                                  required={true}
                                />
                              </div>

                              <div>
                                <RentDurationInputUI
                                  name={`rentDuration-${orderItem.id}-${index}`}
                                  value={rentPrice.number || ''}
                                  onChange={(e) => handleRentPriceChange(orderItem.id, index, 'number', e.target.value)}
                                  periodValue={rentPrice.option || 'day'}
                                  onPeriodChange={(period) => handleRentPriceChange(orderItem.id, index, 'option', period)}
                                  required={true}
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        {(updates.rentPriceAndTime || []).length < 3 && (
                          <button
                            type="button"
                            onClick={() => addRentPrice(orderItem.id, product.currency || 'USD')}
                            className="text-sm text-brand-teal hover:text-brand-teal-dark"
                          >
                            + Add Rental Period
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Assessment Notes */}
                  <div className="md:col-span-2">
                    <FormInput
                      as="textarea"
                      label="Assessment Notes"
                      name={`notes-${orderItem.id}`}
                      value={updates.notes || ''}
                      onChange={(e) => handleFieldChange(orderItem.id, 'notes', e.target.value)}
                      placeholder="Describe the current condition, any damage, or repair needs..."
                      rows={3}
                    />
                  </div>
                  
                  {/* Ready for Relisting */}
                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={updates.readyForRelisting !== false}
                        onChange={(e) => handleFieldChange(orderItem.id, 'readyForRelisting', e.target.checked)}
                        className="h-4 w-4 text-brand-teal focus:ring-brand-teal border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Ready for Relisting 
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={handleBack}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              className="flex items-center"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              Update Product Conditions
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductConditionUpdatePage; 